<template>
  <div class="analytics-container">
    <!-- Modern Header with Glassmorphism -->
    <div class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">
          <v-icon small class="pulse-icon">fiber_manual_record</v-icon>
          <span>LIVE</span>
        </div>
        <h1 class="hero-title">Campaign Analytics</h1>
        <p class="hero-subtitle">Real-time insights & market intelligence</p>
        <div class="hero-stats">
          <div class="stat-pill">
            <v-icon small>trending_up</v-icon>
            <span>+{{ analytics ? analytics.summary.total_intents : 0 }}</span>
          </div>
          <div class="stat-pill">
            <v-icon small>people</v-icon>
            <span>{{ analytics ? analytics.summary.unique_users : 0 }} Users</span>
          </div>
        </div>
      </div>
      <div class="hero-graphic">
        <div class="floating-circle circle-1"></div>
        <div class="floating-circle circle-2"></div>
        <div class="floating-circle circle-3"></div>
      </div>
    </div>

    <!-- Control Panel -->
    <v-card class="control-panel glass-card" elevation="0">
      <v-layout row wrap align-center>
        <v-flex xs12 sm6 pa-2>
          <v-select
            v-model="selectedRange"
            :items="dateRanges"
            label="Time Period"
            @change="loadAnalytics"
            color="teal"
            prepend-icon="date_range"
            solo
            flat
            background-color="rgba(255,255,255,0.05)"
            dark
          ></v-select>
        </v-flex>
        <v-flex xs12 sm6 pa-2 class="text-xs-center text-sm-right">
          <v-btn @click="loadAnalytics" color="teal" depressed large class="modern-btn">
            <v-icon left>refresh</v-icon>
            Refresh Data
          </v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Loading State with Modern Animation -->
    <div v-if="loading" class="loading-container">
      <div class="loader-wrapper">
        <div class="modern-loader"></div>
        <p class="loading-text">Analyzing data...</p>
      </div>
    </div>

    <div v-if="!loading && analytics">
      <!-- KPI Cards Grid -->
      <div class="kpi-grid">
        <div class="kpi-card glass-card" v-for="(kpi, index) in kpiCards" :key="index" :style="{animationDelay: index * 0.1 + 's'}">
          <div class="kpi-icon" :style="{background: kpi.gradient}">
            <v-icon size="28" color="white">{{ kpi.icon }}</v-icon>
          </div>
          <div class="kpi-content">
            <p class="kpi-label">{{ kpi.label }}</p>
            <h2 class="kpi-value">{{ kpi.value }}</h2>
            <div class="kpi-trend" :class="kpi.trendClass">
              <v-icon small>{{ kpi.trendIcon }}</v-icon>
              <span>{{ kpi.trend }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Recurring Client Needs -->
      <v-card class="needs-card glass-card" elevation="0">
        <div class="section-header">
          <div class="header-icon needs-header-icon">
            <v-icon color="white">groups</v-icon>
          </div>
          <div>
            <h3 class="section-title">Recurring Client Needs</h3>
            <p class="section-subtitle">Recurring needs grouped by category — click to analyze &amp; reach out</p>
          </div>
        </div>

        <div v-if="needs.length === 0" class="empty-chart">No recurring needs detected yet</div>

        <div v-else class="needs-list">
          <div
            v-for="need in orderedNeeds"
            :key="need.category"
            class="need-card"
            @click="openNeed(need)"
          >
            <div class="need-top">
              <div class="need-avatar" :style="{background: getCategoryGradient(need.category)}">
                <v-icon size="22" color="white">{{ getCategoryIcon(need.category) }}</v-icon>
              </div>
              <div class="need-main">
                <div class="need-title-row">
                  <span class="need-name">{{ need.label }}</span>
                  <v-icon small class="need-arrow" color="grey">chevron_right</v-icon>
                </div>
                <p class="need-desc">
                  <b>{{ need.client_count }}</b> clients &middot; <b>{{ need.intent_count }}</b> intentions
                  <template v-if="need.avg_amount"> &middot; obj. moy. {{ formatAmountShort(need.avg_amount) }}</template>
                </p>
              </div>
            </div>
            <div class="need-tags">
              <span
                v-for="(u, uKey) in need.urgency_distribution"
                :key="'u-' + uKey"
                class="u-chip"
                :class="'u-' + uKey"
              >
                {{ uKey.toUpperCase() }} {{ u }}
              </span>
            </div>
            <div class="need-kw">
              <span v-for="(kw, kwi) in need.top_keywords.slice(0, 4)" :key="'nk-' + kwi" class="kw-tag">{{ kw }}</span>
              <span v-if="need.suggested_products.length" class="prod-tag">
                <v-icon x-small>shopping_bag</v-icon>
                {{ need.suggested_products.length }} product(s)
              </span>
            </div>
          </div>
        </div>
      </v-card>

      <!-- AI Strategic Insights (collapsible → popup) -->
      <v-card class="insights-card glass-card" elevation="0" v-if="analytics.ai_insights">
        <div class="section-header" @click="insightsDialog = true">
          <div class="header-icon">
            <v-icon color="white">psychology</v-icon>
          </div>
          <div class="insight-collapsed-text">
            <h3 class="section-title">AI Strategic Insights</h3>
            <p class="section-subtitle">What the data reveals about your clients — in plain language</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn color="teal" dark depressed class="modern-btn view-insights-btn">
            <v-icon left>visibility</v-icon>
            View Insights
          </v-btn>
        </div>
      </v-card>

      <!-- Distribution Charts -->
      <v-layout row wrap class="charts-section">
        <v-flex xs12 md6 pa-2>
          <v-card class="chart-card glass-card" elevation="0">
            <div class="chart-header">
              <v-icon color="purple">pie_chart</v-icon>
              <h4>Campaign Categories</h4>
            </div>
            <div class="chart-content" v-if="Object.keys(analytics.category_distribution).length > 0">
              <div v-for="(count, category) in analytics.category_distribution" :key="category" class="progress-item">
                <div class="progress-label">
                  <span class="category-name">{{ category }}</span>
                  <span class="category-count">{{ count }}</span>
                </div>
                <div class="modern-progress">
                  <div
                    class="progress-fill"
                    :style="{
                      width: (count / analytics.summary.total_intents) * 100 + '%',
                      background: getCategoryGradient(category)
                    }"
                  >
                    <span class="progress-percentage">{{ ((count / analytics.summary.total_intents) * 100).toFixed(1) }}%</span>
                  </div>
                </div>
              </div>
            </div>
            <p v-else class="empty-chart">No data available</p>
          </v-card>
        </v-flex>

        <v-flex xs12 md6 pa-2>
          <v-card class="chart-card glass-card" elevation="0">
            <div class="chart-header">
              <v-icon color="orange">speed</v-icon>
              <h4>Urgency Levels</h4>
            </div>
            <div class="chart-content" v-if="Object.keys(analytics.urgency_distribution).length > 0">
              <div v-for="(count, urgency) in analytics.urgency_distribution" :key="urgency" class="urgency-item">
                <div class="urgency-badge" :class="'urgency-' + urgency">
                  <v-icon small>{{ getUrgencyIcon(urgency) }}</v-icon>
                  <span>{{ urgency.toUpperCase() }}</span>
                </div>
                <div class="urgency-bar">
                  <div
                    class="urgency-fill"
                    :style="{
                      width: (count / analytics.summary.total_intents) * 100 + '%',
                      background: getUrgencyGradient(urgency)
                    }"
                  ></div>
                </div>
                <span class="urgency-count">{{ count }}</span>
              </div>
            </div>
            <p v-else class="empty-chart">No data available</p>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Most Demanded Products -->
      <v-card class="products-card glass-card" elevation="0">
        <div class="section-header">
          <div class="header-icon">
            <v-icon color="white">shopping_cart</v-icon>
          </div>
          <div>
            <h3 class="section-title">Most Demanded Products</h3>
            <p class="section-subtitle">Top products by user interest</p>
          </div>
        </div>

        <v-text-field
          v-model="productSearch"
          prepend-inner-icon="search"
          label="Search products..."
          solo
          flat
          hide-details
          class="search-field"
          background-color="rgba(255,255,255,0.05)"
          dark
        ></v-text-field>

        <div class="products-grid">
          <div
            v-for="(product, index) in filteredProducts"
            :key="product.product_id"
            class="product-card"
            @click="viewProductDetails(product)"
          >
            <div class="product-rank">
              <div class="rank-badge" :class="getRankClass(index + 1)">
                <span class="rank-number">#{{ index + 1 }}</span>
              </div>
            </div>
            <div class="product-image">
              <img v-if="product.product_image" :src="product.product_image" :alt="product.product_name">
              <div v-else class="product-placeholder">
                <v-icon size="40">shopping_bag</v-icon>
              </div>
              <div class="product-badge">
                <v-icon small>local_fire_department</v-icon>
                <span>{{ product.campaign_count }}</span>
              </div>
            </div>
            <div class="product-info">
              <h4 class="product-name">{{ product.product_name }}</h4>
              <div class="demand-indicator-mini">
                <v-icon x-small color="teal">trending_up</v-icon>
                <span>{{ product.campaign_count }} demandes</span>
              </div>
              <div class="product-stats">
                <div class="stat-mini">
                  <v-icon small>people</v-icon>
                  <span>{{ product.unique_users }}</span>
                </div>
                <div class="stat-mini success-stat">
                  <v-icon small>check_circle</v-icon>
                  <span>{{ product.success_rate.toFixed(0) }}%</span>
                </div>
              </div>
              <div class="product-amount">
                <span class="amount-label">Avg Target</span>
                <span class="amount-value">{{ formatAmount(product.avg_target_amount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="filteredProducts.length === 0" class="empty-products">
          <v-icon size="80" color="grey">inventory_2</v-icon>
          <p>No products found</p>
        </div>
      </v-card>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !analytics" class="empty-state-container">
      <div class="empty-illustration">
        <v-icon size="120" color="grey">analytics</v-icon>
      </div>
      <h3>No Data Available</h3>
      <p>Start collecting campaign data to see analytics</p>
    </div>

    <!-- Product Detail Modal -->
    <v-dialog v-model="productDialog" max-width="500px" content-class="modern-dialog">
      <v-card class="product-detail-card" v-if="selectedProduct">
        <div class="detail-header" :style="{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}">
          <v-btn icon dark @click="productDialog = false" class="close-btn">
            <v-icon>close</v-icon>
          </v-btn>
          <div class="detail-image">
            <img v-if="selectedProduct.product_image" :src="selectedProduct.product_image" :alt="selectedProduct.product_name">
            <div v-else class="detail-placeholder">
              <v-icon size="80" color="white">shopping_bag</v-icon>
            </div>
          </div>
        </div>

        <div class="detail-content">
          <h2 class="detail-title">{{ selectedProduct.product_name }}</h2>
          <p class="detail-id">Product ID: {{ selectedProduct.product_id }}</p>

          <div class="detail-metrics">
            <div class="metric-card rank-metric">
              <v-icon color="orange">emoji_events</v-icon>
              <div>
                <p class="metric-value">#{{ getProductRank(selectedProduct.product_id) }}</p>
                <p class="metric-label">Classement</p>
              </div>
            </div>
            <div class="metric-card">
              <v-icon color="blue">campaign</v-icon>
              <div>
                <p class="metric-value">{{ selectedProduct.campaign_count }}</p>
                <p class="metric-label">Demandes</p>
              </div>
            </div>
            <div class="metric-card">
              <v-icon color="purple">people</v-icon>
              <div>
                <p class="metric-value">{{ selectedProduct.unique_users }}</p>
                <p class="metric-label">Utilisateurs</p>
              </div>
            </div>
            <div class="metric-card">
              <v-icon color="green">check_circle</v-icon>
              <div>
                <p class="metric-value">{{ selectedProduct.success_rate.toFixed(1) }}%</p>
                <p class="metric-label">Succès</p>
              </div>
            </div>
          </div>

          <div class="detail-amounts">
            <div class="amount-box">
              <p class="amount-label">Total Target</p>
              <p class="amount-value">{{ formatAmount(selectedProduct.total_target_amount) }}</p>
            </div>
            <div class="amount-box">
              <p class="amount-label">Average Target</p>
              <p class="amount-value">{{ formatAmount(selectedProduct.avg_target_amount) }}</p>
            </div>
          </div>

          <div class="detail-progress">
            <div class="progress-row">
              <span>Completed</span>
              <span class="progress-count">{{ selectedProduct.completed_campaigns }}</span>
            </div>
            <div class="progress-row">
              <span>Ongoing</span>
              <span class="progress-count">{{ selectedProduct.ongoing_campaigns }}</span>
            </div>
          </div>

          <div class="demand-indicator">
            <v-icon color="orange">local_fire_department</v-icon>
            <div class="demand-text">
              <span class="demand-title">Produit Très Demandé</span>
              <span class="demand-count">{{ selectedProduct.campaign_count }} campagnes créées</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- AI Insights Popup -->
    <v-dialog v-model="insightsDialog" max-width="720px" content-class="modern-dialog">
      <v-card class="insights-popup-card" v-if="analytics && analytics.ai_insights">
        <div class="popup-header">
          <div class="popup-header-icon">
            <v-icon color="white">psychology</v-icon>
          </div>
          <div>
            <h3 class="popup-title">AI Strategic Insights</h3>
            <p class="popup-subtitle">What the data reveals about your clients</p>
          </div>
          <v-btn icon dark @click="insightsDialog = false" class="popup-close">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
        <div class="popup-body">
          <v-layout row wrap class="insights-grid">
            <v-flex xs12 md6 pa-2>
              <div class="insight-box trend-box">
                <div class="insight-header">
                  <v-icon color="teal">trending_up</v-icon>
                  <h4>Key Trends</h4>
                </div>
                <div class="insight-content">
                  <div v-for="(trend, index) in getInsightItems('key_trends')" :key="'trend-'+index" class="insight-item">
                    <div class="item-dot"></div>
                    <span>{{ trend }}</span>
                  </div>
                  <p v-if="getInsightItems('key_trends').length === 0" class="empty-state">No trends detected</p>
                </div>
              </div>
            </v-flex>

            <v-flex xs12 md6 pa-2>
              <div class="insight-box opportunity-box">
                <div class="insight-header">
                  <v-icon color="blue">lightbulb</v-icon>
                  <h4>Opportunities</h4>
                </div>
                <div class="insight-content">
                  <div v-for="(opp, index) in getInsightItems('opportunities')" :key="'opp-'+index" class="insight-item">
                    <div class="item-dot"></div>
                    <span>{{ opp }}</span>
                  </div>
                  <p v-if="getInsightItems('opportunities').length === 0" class="empty-state">Analyzing opportunities...</p>
                </div>
              </div>
            </v-flex>

            <v-flex xs12 md6 pa-2>
              <div class="insight-box risk-box">
                <div class="insight-header">
                  <v-icon color="orange">warning</v-icon>
                  <h4>Risk Areas</h4>
                </div>
                <div class="insight-content">
                  <div v-for="(risk, index) in getInsightItems('risk_areas')" :key="'risk-'+index" class="insight-item">
                    <div class="item-dot"></div>
                    <span>{{ risk }}</span>
                  </div>
                  <p v-if="getInsightItems('risk_areas').length === 0" class="empty-state">No significant risks</p>
                </div>
              </div>
            </v-flex>

            <v-flex xs12 md6 pa-2>
              <div class="insight-box recommendation-box">
                <div class="insight-header">
                  <v-icon color="green">recommend</v-icon>
                  <h4>Recommendations</h4>
                </div>
                <div class="insight-content">
                  <div v-for="(rec, index) in getInsightItems('recommendations')" :key="'rec-'+index" class="insight-item">
                    <div class="item-dot"></div>
                    <span>{{ rec }}</span>
                  </div>
                  <p v-if="getInsightItems('recommendations').length === 0" class="empty-state">Review data collection</p>
                </div>
              </div>
            </v-flex>
          </v-layout>
        </div>
      </v-card>
    </v-dialog>

    <!-- Recurring Need Analysis Dialog (clients + products + embedded chat) -->
    <recurring-needs-dialog :dialog.sync="needDialog" :need="selectedNeed" />
  </div>
</template>

<script>
import axios from 'axios'
import RecurringNeedsDialog from './RecurringNeedsDialog.vue'

export default {
  components: {
    RecurringNeedsDialog
  },

  data() {
    return {
      loading: false,
      analytics: null,
      needs: [],
      selectedRange: 0,
      dateRanges: [
        { text: 'All Time', value: 0 },
        { text: 'Last 7 Days', value: 7 },
        { text: 'Last 30 Days', value: 30 },
        { text: 'Last 90 Days', value: 90 },
        { text: 'Last 180 Days', value: 180 },
        { text: 'Last Year', value: 365 }
      ],
      productSearch: '',
      productDialog: false,
      selectedProduct: null,
      topProducts: [],
      needDialog: false,
      selectedNeed: null,
      insightsDialog: false
    }
  },

  computed: {
    kpiCards() {
      if (!this.analytics) return []
      return [
        {
          icon: 'campaign',
          label: 'Total Campaigns',
          value: this.analytics.summary.total_intents,
          trend: '+12%',
          trendIcon: 'trending_up',
          trendClass: 'trend-up',
          gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        {
          icon: 'people',
          label: 'Active Users',
          value: this.analytics.summary.unique_users,
          trend: '+8%',
          trendIcon: 'trending_up',
          trendClass: 'trend-up',
          gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
        },
        {
          icon: 'attach_money',
          label: 'Avg Goal',
          value: this.formatAmountShort(this.analytics.summary.average_goal_amount),
          trend: '+5%',
          trendIcon: 'trending_up',
          trendClass: 'trend-up',
          gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
        },
        {
          icon: 'pending_actions',
          label: 'Non-analysées',
          value: this.analytics.summary.pending_campaigns != null ? this.analytics.summary.pending_campaigns : 0,
          trend: this.analytics.summary.date_range_days === 0 ? 'Toute période' : this.analytics.summary.date_range_days + ' jours',
          trendIcon: 'schedule',
          trendClass: 'trend-neutral',
          gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
        }
      ]
    },

    topKeywords() {
      if (!this.analytics || !this.analytics.top_keywords) return {}
      const entries = Object.entries(this.analytics.top_keywords)
      return Object.fromEntries(entries.slice(0, 15))
    },

    filteredProducts() {
      if (!this.productSearch) return this.topProducts
      const search = this.productSearch.toLowerCase()
      return this.topProducts.filter(p =>
        p.product_name.toLowerCase().includes(search)
      )
    },

    orderedNeeds() {
      return [...this.needs].sort((a, b) => {
        const aOther = a.category === 'other' || a.category === 'uncategorized' ? 1 : 0
        const bOther = b.category === 'other' || b.category === 'uncategorized' ? 1 : 0
        if (aOther !== bOther) return aOther - bOther
        return b.intent_count - a.intent_count
      })
    }
  },

  mounted() {
    document.title = "PettyCash | Analytics Dashboard"
    this.loadAnalytics()
  },

  watch: {
    needDialog() { this.updateFloatChat() },
    productDialog() { this.updateFloatChat() },
    insightsDialog() { this.updateFloatChat() },
    $route() {
      if (this.$route.path !== '/savings/manager/analytics') {
        this.$store.commit('setHideFloatChat', false)
      }
    }
  },

  beforeDestroy() {
    this.$store.commit('setHideFloatChat', false)
  },

  methods: {
    async loadAnalytics() {
      this.loading = true
      this.$store.commit('setIsLoading', true)

      try {
        const response = await axios.get(
          `/api/v1/savings/intent/analytics/?days=${this.selectedRange}`
        )
        this.analytics = response.data
        await this.loadTopProducts()
        await this.loadNeeds()
      } catch (error) {
        console.error('Error loading analytics:', error)
        this.analytics = {
          summary: {
            total_intents: 0,
            unique_users: 0,
            average_goal_amount: 0,
            date_range_days: this.selectedRange,
            pending_campaigns: 0
          },
          ai_insights: {
            key_trends: [],
            opportunities: [],
            risk_areas: [],
            recommendations: []
          },
          category_distribution: {},
          urgency_distribution: {},
          top_keywords: {},
          recent_intents: []
        }
        this.needs = []

        if (error.response) {
          this.$store.commit('setSnackBarMessage', error.response.data.error || 'Failed to load analytics')
          this.$store.commit('setSnackBarColor', 'red darken-4')
          this.$store.commit('activateSnackBar', true)
        }
      }

      this.loading = false
      this.$store.commit('setIsLoading', false)
    },

    async loadTopProducts() {
      try {
        const response = await axios.get(`/api/v1/savings/campaigns/top-products/?days=${this.selectedRange}`)

        if (response.data && response.data.top_products) {
          this.topProducts = response.data.top_products.map(product => ({
            product_id: product.market_item_id,
            product_name: product.product_name || `Product ${product.market_item_id}`,
            product_image: product.product_image || null,
            campaign_count: product.campaign_count || 0,
            unique_users: product.unique_users || 0,
            avg_target_amount: product.avg_target_amount || 0,
            total_target_amount: product.total_target_amount || 0,
            completed_campaigns: product.completed_campaigns || 0,
            ongoing_campaigns: product.ongoing_campaigns || 0,
            success_rate: product.success_rate || 0,
            category: product.category || null
          }))
        }
      } catch (error) {
        console.error('Error loading top products:', error)
        this.topProducts = []
      }
    },

    async loadNeeds() {
      try {
        const response = await axios.get(`/api/v1/savings/intent/needs/?days=${this.selectedRange}`)
        this.needs = (response.data && response.data.needs) || []
      } catch (error) {
        console.error('Error loading recurring needs:', error)
        this.needs = []
      }
    },

    getInsightItems(key) {
      if (!this.analytics || !this.analytics.ai_insights || !this.analytics.ai_insights[key]) {
        return []
      }
      const items = this.analytics.ai_insights[key]
      return Array.isArray(items) ? items : []
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA'
    },

    formatAmountShort(amount) {
      if (amount >= 1000000) {
        return (amount / 1000000).toFixed(1) + 'M'
      } else if (amount >= 1000) {
        return (amount / 1000).toFixed(0) + 'K'
      }
      return amount.toString()
    },

    getCategoryGradient(category) {
      const gradients = {
        'education': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'business': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'health': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'property': 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
        'technology': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        'lifestyle': 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        'emergency': 'linear-gradient(135deg, #ffa751 0%, #ffe259 100%)',
        'investment': 'linear-gradient(135deg, #21d4fd 0%, #b721ff 100%)',
        'travel': 'linear-gradient(135deg, #08aeea 0%, #2af598 100%)',
        'other': 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)'
      }
      return gradients[category] || gradients['other']
    },

    getUrgencyGradient(urgency) {
      const gradients = {
        'critical': 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
        'high': 'linear-gradient(135deg, #ffa751 0%, #ffe259 100%)',
        'medium': 'linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%)',
        'low': 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
      }
      return gradients[urgency] || gradients['medium']
    },

    getUrgencyIcon(urgency) {
      const icons = {
        'critical': 'priority_high',
        'high': 'arrow_upward',
        'medium': 'remove',
        'low': 'arrow_downward'
      }
      return icons[urgency] || 'remove'
    },

    viewProductDetails(product) {
      this.selectedProduct = product
      this.productDialog = true
    },

    getProductRank(productId) {
      const index = this.topProducts.findIndex(p => p.product_id === productId)
      return index !== -1 ? index + 1 : '-'
    },

    getRankClass(rank) {
      if (rank === 1) return 'rank-gold'
      if (rank === 2) return 'rank-silver'
      if (rank === 3) return 'rank-bronze'
      return 'rank-default'
    },

    openNeed(need) {
      this.selectedNeed = need
      this.needDialog = true
    },

    updateFloatChat() {
      const anyOpen = this.needDialog || this.productDialog || this.insightsDialog
      this.$store.commit('setHideFloatChat', anyOpen)
    },

    getCategoryIcon(category) {
      const icons = {
        'education': 'school',
        'business': 'business_center',
        'health': 'local_hospital',
        'property': 'home_work',
        'technology': 'devices',
        'lifestyle': 'spa',
        'emergency': 'emergency',
        'investment': 'trending_up',
        'travel': 'flight',
        'transport': 'directions_car',
        'other': 'category'
      }
      return icons[category] || 'category'
    },

    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(/[\s._-]+/).filter(Boolean)
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },

    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const pad = n => String(n).padStart(2, '0')
      return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear()
    },

    truncate(text, max) {
      if (!text) return ''
      return text.length > max ? text.substring(0, max) + '…' : text
    }
  }
}
</script>

<style scoped>
.analytics-container {
  background: transparent;
  min-height: 100vh;
  padding: 16px;
  padding-bottom: 80px;
}

/* Hero Section */
.hero-section {
  position: relative;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.1) 0%, rgba(0, 121, 107, 0.15) 100%);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 24px 20px;
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 150, 136, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(239, 68, 68, 0.2);
  backdrop-filter: blur(10px);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.pulse-icon {
  animation: pulse 2s infinite;
  color: #ef4444;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.hero-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #00695c 0%, #00897b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 20px;
}

.hero-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 150, 136, 0.15);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid rgba(0, 150, 136, 0.2);
  color: #00695c;
}

.hero-graphic {
  position: absolute;
  top: 0;
  right: 0;
  width: 200px;
  height: 200px;
  z-index: 1;
}

.floating-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.2), rgba(0, 121, 107, 0.3));
  animation: float 6s infinite ease-in-out;
}

.circle-1 {
  width: 80px;
  height: 80px;
  top: 20px;
  right: 20px;
}

.circle-2 {
  width: 60px;
  height: 60px;
  top: 80px;
  right: 80px;
  animation-delay: 1s;
}

.circle-3 {
  width: 40px;
  height: 40px;
  top: 120px;
  right: 40px;
  animation-delay: 2s;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

/* Glass Card Effect */
.glass-card {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  border: 1px solid rgba(0, 150, 136, 0.15);
  margin-bottom: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
}

/* Control Panel */
.control-panel {
  padding: 16px;
}

.modern-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

/* Loading State */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
}

.loader-wrapper {
  text-align: center;
}

.modern-loader {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
}

/* KPI Cards */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.kpi-card {
  padding: 20px;
  animation: slideUp 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.kpi-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
  font-weight: 500;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #212121;
}

.kpi-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
}

.trend-up {
  color: #10b981;
}

.trend-down {
  color: #ef4444;
}

.trend-neutral {
  color: #f59e0b;
}

/* Insights Section */
.insights-card {
  padding: 24px;
}

.insights-card .section-header {
  cursor: pointer;
  flex-wrap: wrap;
  gap: 12px;
}

.insights-card .section-header:hover .section-title {
  color: #00897b;
}

.insight-collapsed-text {
  flex: 1;
  min-width: 200px;
}

.view-insights-btn {
  min-width: 150px;
}

/* AI Insights Popup */
.insights-popup-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border-radius: 24px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 150, 136, 0.2);
  color: #212121;
}

.popup-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.popup-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.popup-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.popup-subtitle {
  font-size: 13px;
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.9);
}

.popup-close {
  position: absolute;
  top: 14px;
  right: 14px;
}

.popup-body {
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #212121;
}

.section-subtitle {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin: 4px 0 0;
}

.insights-grid {
  gap: 16px;
}

.insight-box {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(0, 150, 136, 0.1);
  transition: all 0.3s ease;
}

.insight-box:hover {
  transform: translateY(-4px);
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.insight-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.insight-header h4 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #212121;
}

.insight-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.insight-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.75);
}

.item-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  margin-top: 8px;
  flex-shrink: 0;
}

.empty-state {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
}

/* Charts Section */
.charts-section {
  margin-bottom: 24px;
}

.chart-card {
  padding: 24px;
  min-height: 300px;
}

.chart-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.chart-header h4 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #212121;
}

.chart-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.progress-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.75);
}

.category-name {
  font-weight: 600;
  text-transform: capitalize;
}

.category-count {
  font-weight: 700;
  color: #212121;
}

.modern-progress {
  height: 40px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 12px;
  transition: width 0.6s ease;
  position: relative;
}

.progress-percentage {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Urgency Items */
.urgency-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 12px;
}

.urgency-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  min-width: 100px;
}

.urgency-critical {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.urgency-high {
  background: rgba(251, 146, 60, 0.15);
  color: #ea580c;
}

.urgency-medium {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}

.urgency-low {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.urgency-bar {
  flex: 1;
  height: 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  overflow: hidden;
}

.urgency-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.urgency-count {
  font-size: 16px;
  font-weight: 700;
  min-width: 40px;
  text-align: right;
  color: #212121;
}

/* Keywords Cloud */
.keywords-card {
  padding: 24px;
}

.keywords-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 0;
}

.keyword-bubble {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.15), rgba(0, 121, 107, 0.2));
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid rgba(0, 150, 136, 0.2);
  transition: all 0.3s ease;
  cursor: default;
  color: #00695c;
}

.keyword-bubble:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.25), rgba(0, 121, 107, 0.3));
}

.keyword-count {
  background: rgba(0, 150, 136, 0.2);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
}

/* Products Section */
.products-card {
  padding: 20px;
}

.search-field {
  margin-bottom: 20px;
  border-radius: 12px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  margin-top: 20px;
}

.product-card {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 150, 136, 0.15);
  position: relative;
}

.product-card:hover {
  transform: translateY(-8px);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  border-color: rgba(0, 150, 136, 0.3);
}

.product-rank {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}

.rank-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 2px solid white;
}

.rank-number {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.rank-gold {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #b8860b;
}

.rank-silver {
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
  color: #5a5a5a;
}

.rank-bronze {
  background: linear-gradient(135deg, #cd7f32 0%, #d4a574 100%);
  color: #654321;
}

.rank-default {
  background: linear-gradient(135deg, #78909c 0%, #90a4ae 100%);
  color: white;
}

.product-image {
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.15), rgba(0, 121, 107, 0.2));
  overflow: hidden;
}

.product-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(239, 68, 68, 0.9);
  backdrop-filter: blur(10px);
  padding: 4px 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
}

.product-info {
  padding: 12px;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #212121;
}

.demand-indicator-mini {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #00897b;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 4px 8px;
  background: rgba(0, 150, 136, 0.1);
  border-radius: 8px;
  width: fit-content;
}

.product-stats {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.stat-mini {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.05);
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #424242;
}

.success-stat {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.product-amount {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.amount-label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
}

.amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #00897b;
}

/* Product Detail Modal */
.modern-dialog {
  background: transparent !important;
  box-shadow: none !important;
}

.product-detail-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border-radius: 24px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 150, 136, 0.2);
  color: #212121;
}

.detail-header {
  padding: 32px 24px;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
}

.detail-image {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.detail-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-content {
  padding: 24px;
  color: #212121;
}

.detail-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px;
  text-align: center;
  color: #212121;
}

.detail-id {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
  margin-bottom: 24px;
}

.detail-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.metric-card {
  background: rgba(0, 150, 136, 0.05);
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  border: 1px solid rgba(0, 150, 136, 0.1);
}

.rank-metric {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 160, 0, 0.15));
  border-color: rgba(255, 193, 7, 0.3);
}

.metric-value {
  font-size: 20px;
  font-weight: 700;
  margin: 8px 0 4px;
  color: #212121;
}

.metric-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
}

.detail-amounts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.amount-box {
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.1), rgba(0, 121, 107, 0.15));
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(0, 150, 136, 0.2);
}

.amount-box .amount-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
}

.amount-box .amount-value {
  font-size: 16px;
  font-weight: 700;
  color: #00897b;
}

.detail-progress {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.progress-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  color: #424242;
}

.progress-row:last-child {
  border-bottom: none;
}

.progress-count {
  font-weight: 700;
  color: #212121;
}

.demand-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 193, 7, 0.15));
  padding: 16px;
  border-radius: 12px;
  font-weight: 600;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.demand-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.demand-title {
  font-size: 14px;
  font-weight: 700;
  color: #e65100;
}

.demand-count {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

/* Empty States */
.empty-chart,
.empty-products {
  text-align: center;
  padding: 60px 20px;
  color: rgba(0, 0, 0, 0.4);
}

.empty-state-container {
  text-align: center;
  padding: 100px 20px;
}

.empty-illustration {
  margin-bottom: 24px;
  opacity: 0.3;
}

.empty-state-container h3 {
  font-size: 24px;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.empty-state-container p {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.4);
}

/* Mobile Optimizations */
@media (max-width: 960px) {
  .analytics-container {
    padding: 12px;
  }

  .hero-section {
    padding: 20px 16px;
  }

  .hero-title {
    font-size: 26px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .hero-graphic {
    display: none;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .kpi-value {
    font-size: 24px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-subtitle {
    font-size: 13px;
  }

  .insights-grid .v-flex {
    padding: 8px !important;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .detail-metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .keywords-cloud {
    justify-content: center;
  }

  .keyword-bubble {
    font-size: 12px;
  }

  .chart-card {
    min-height: 250px;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 22px;
  }

  .kpi-card {
    padding: 16px;
  }

  .kpi-icon {
    width: 48px;
    height: 48px;
  }

  .kpi-value {
    font-size: 20px;
  }

  .stat-pill {
    font-size: 12px;
    padding: 6px 12px;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .product-card {
    border-radius: 12px;
  }

  .rank-badge {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .product-info {
    padding: 10px;
  }

  .product-name {
    font-size: 13px;
  }

  .demand-indicator-mini {
    font-size: 11px;
  }

  .detail-amounts {
    grid-template-columns: 1fr;
  }

  .chart-content {
    gap: 12px;
  }

  .modern-progress {
    height: 32px;
  }

  .control-panel .v-flex {
    padding: 8px !important;
  }

  .modern-btn {
    width: 100%;
  }
}

@media (max-width: 400px) {
  .products-grid {
    grid-template-columns: 1fr;
  }

  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

/* Recurring Client Needs */
.needs-card {
  padding: 24px;
}

.needs-header-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.needs-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.need-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  border: 1px solid rgba(0, 150, 136, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;
}

.need-card:hover {
  transform: translateX(6px);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 150, 136, 0.35);
}

.need-top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.need-avatar {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.need-main {
  flex: 1;
  min-width: 0;
}

.need-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.need-name {
  font-size: 16px;
  font-weight: 700;
  color: #212121;
}

.need-desc {
  margin: 4px 0 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}

.need-desc b {
  color: #00897b;
}

.need-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.u-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 8px;
}

.u-chip.u-critical { background: rgba(239, 68, 68, 0.15); color: #dc2626; }
.u-chip.u-high { background: rgba(251, 146, 60, 0.15); color: #ea580c; }
.u-chip.u-medium { background: rgba(59, 130, 246, 0.15); color: #2563eb; }
.u-chip.u-low { background: rgba(34, 197, 94, 0.15); color: #16a34a; }
.u-chip.u-unknown { background: rgba(0, 0, 0, 0.08); color: rgba(0, 0, 0, 0.5); }

.need-kw {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.kw-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 8px;
  background: rgba(102, 126, 234, 0.12);
  color: #4f63d6;
}

.prod-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 8px;
  background: rgba(0, 150, 136, 0.1);
  color: #00897b;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

/* Intent Detail Dialog */
.intent-detail-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border-radius: 24px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 150, 136, 0.2);
  color: #212121;
}

.intent-header {
  padding: 24px 24px 28px;
  position: relative;
}

.intent-user-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 20px;
}

.intent-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.intent-avatar-text {
  font-weight: 800;
  font-size: 15px;
  color: #333;
}

.intent-user {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.intent-meta {
  font-size: 13px;
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.9);
}

.intent-body {
  padding: 20px 24px 24px;
}

.intent-quote-box {
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.08), rgba(0, 121, 107, 0.12));
  border-radius: 14px;
  padding: 16px;
  border-left: 4px solid #00897b;
  margin-bottom: 16px;
}

.intent-quote {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  font-style: italic;
  color: #333;
}

.intent-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.urgency-tag-large,
.chip-goal,
.chip-prob,
.chip-campaign {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  color: #424242;
}

.urgency-tag-large.ure-critical { background: rgba(239, 68, 68, 0.15); color: #dc2626; }
.urgency-tag-large.ure-high { background: rgba(251, 146, 60, 0.15); color: #ea580c; }
.urgency-tag-large.ure-medium { background: rgba(59, 130, 246, 0.15); color: #2563eb; }
.urgency-tag-large.ure-low { background: rgba(34, 197, 94, 0.15); color: #16a34a; }

.chip-goal { background: rgba(102, 126, 234, 0.12); color: #4f63d6; }
.chip-prob { background: rgba(34, 197, 94, 0.12); color: #16a34a; }
.chip-campaign { background: rgba(255, 152, 0, 0.12); color: #e65100; }

.intent-section {
  margin-bottom: 18px;
}

.intent-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.intent-section-head h4 {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  color: #212121;
}

.intent-list {
  margin: 0;
  padding-left: 20px;
}

.intent-list li {
  font-size: 13.5px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.75);
  margin-bottom: 6px;
}

.risk-section .intent-list li {
  color: #b45309;
}

.intent-kw-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.kw-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
  color: #4f63d6;
}

.scenario-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scenario-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 150, 136, 0.15);
  border-radius: 12px;
}

.scenario-icon {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scenario-label {
  font-size: 13.5px;
  font-weight: 700;
  margin: 0 0 2px;
  color: #212121;
}

.scenario-desc {
  font-size: 12.5px;
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.5;
}

.intent-actions {
  margin-top: 20px;
}

.intent-actions .v-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 150, 136, 0.3);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 150, 136, 0.5);
}

/* Loading animation for text */
.loading-text {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.6);
  animation: pulse-text 1.5s infinite;
}

@keyframes pulse-text {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
